import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertCircle, KeyRound, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const ForceChangePasswordPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('A nova password deve ter pelo menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirm) {
      setError('As passwords não coincidem.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      const updated = await refreshUser();
      navigate((updated?.role || user?.role) === 'super_admin' ? '/super-admin' : '/admin', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível alterar a password.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2">
            <Shield className="w-10 h-10 text-slate-900" />
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              COGNIEtica
            </span>
          </span>
        </div>

        <Card data-testid="force-change-password-page">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
              <KeyRound className="w-7 h-7 text-amber-600" />
            </div>
            <CardTitle>Defina uma nova password</CardTitle>
            <CardDescription>
              A sua password foi reposta por um administrador. Por segurança, defina uma nova password para continuar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <AlertDescription className="text-red-700" data-testid="force-change-error">{error}</AlertDescription>
                </Alert>
              )}
              <div>
                <Label htmlFor="current">Password atual (temporária)</Label>
                <Input
                  id="current"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="force-current-password-input"
                />
              </div>
              <div>
                <Label htmlFor="new">Nova password</Label>
                <Input
                  id="new"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  data-testid="force-new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm">Confirmar nova password</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  data-testid="force-confirm-password-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="force-change-submit-btn">
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A guardar...</>
                ) : (
                  'Guardar nova password'
                )}
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={handleLogout} data-testid="force-change-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Terminar sessão
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForceChangePasswordPage;
